// src/components/Dashboard/CoachDashboard/MyTeamsWidget.jsx
// Shows teams where user is a coach

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { FaUsers, FaTrophy, FaChartBar, FaArrowRight } from 'react-icons/fa';
import styles from './MyTeamsWidget.module.css';

const MyTeamsWidget = ({ program, userProfile, refreshTrigger }) => {
  const navigate = useNavigate();
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch teams
  useEffect(() => {
    const fetchTeams = async () => {
      if (!program?._id || !userProfile?._id) {
        setTeams([]);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
        const auth = getAuth();
        const token = auth.currentUser ? await auth.currentUser.getIdToken() : null;
        
        const headers = token ? {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        } : {};
        
        const response = await fetch(`${backendUrl}/api/teams/program/${program._id}`, { headers });
        
        if (!response.ok) {
          setTeams([]);
          return;
        }

        const allTeams = await response.json();
        const teamsArray = Array.isArray(allTeams) ? allTeams : [allTeams];
        
        // Filter teams where current user is coach or assistant coach
        const myTeams = teamsArray.filter(team => {
          const isHeadCoach = team.coach?.userId === userProfile._id || 
                              team.coach?.userId?._id === userProfile._id;
          const isAssistantCoach = team.assistantCoaches?.some(ac => 
            ac.userId === userProfile._id || ac.userId?._id === userProfile._id
          );
          return isHeadCoach || isAssistantCoach;
        }).map(team => {
          const isHeadCoach = team.coach?.userId === userProfile._id || 
                              team.coach?.userId?._id === userProfile._id;
          return {
            ...team,
            coachRole: isHeadCoach ? 'HC' : 'AC',
            coachRoleFull: isHeadCoach ? 'Head Coach' : 'Assistant Coach'
          };
        });

        setTeams(myTeams);
      } catch (error) {
        console.error('Error fetching teams:', error);
        setTeams([]);
      } finally {
        setLoading(false);
      }
    };

    fetchTeams();
  }, [program?._id, userProfile?._id, refreshTrigger]);

  const getWinRate = (team) => {
    const metrics = team.performanceMetrics || {};
    const wins = metrics.wins || 0;
    const losses = metrics.losses || 0;
    const draws = metrics.draws || 0;
    const total = wins + losses + draws;
    if (total === 0) return 0;
    return Math.round((wins / total) * 100);
  };

  const getRecord = (team) => {
    const metrics = team.performanceMetrics || {};
    const wins = metrics.wins || 0;
    const losses = metrics.losses || 0;
    const draws = metrics.draws || 0;
    return `W${wins} L${losses} D${draws}`;
  };

  const handleTeamClick = (team) => {
    // Navigate to teams page with specific team highlighted
    navigate(`/program/${program._id}/teams?teamId=${team._id}`);
  };

  const handleViewAllTeams = () => {
    navigate(`/program/${program._id}/teams`);
  };

  const handleRoster = (e, team) => {
    e.stopPropagation();
    navigate(`/program/${program._id}/teams?teamId=${team._id}&view=roster`);
  };

  const handleStats = (e, team) => {
    e.stopPropagation();
    navigate(`/program/${program._id}/teams?teamId=${team._id}&view=stats`);
  };

  // Loading state
  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <FaUsers className={styles.headerIcon} />
          <h3 className={styles.headerTitle}>My Teams</h3>
        </div>
        <div className={styles.loadingState}>
          <div className={styles.loadingSpinner}></div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <FaUsers className={styles.headerIcon} />
        <h3 className={styles.headerTitle}>My Teams</h3>
        {teams.length > 0 && (
          <span className={styles.badge}>{teams.length}</span>
        )}
      </div>

      <div className={styles.content}>
        {teams.length === 0 ? (
          <div className={styles.emptyState}>
            <span className={styles.emptyIcon}>👥</span>
            <p>No teams assigned</p>
            <span className={styles.emptyHint}>You'll see your teams here</span>
          </div>
        ) : (
          <div className={styles.teamsList}>
            {teams.map(team => (
              <div 
                key={team._id} 
                className={styles.teamCard}
                onClick={() => handleTeamClick(team)}
              >
                <div className={styles.teamHeader}>
                  <span className={styles.teamIcon}>⚽</span>
                  <div className={styles.teamInfo}>
                    <h4 className={styles.teamName}>{team.name}</h4>
                    <span className={styles.teamMeta}>
                      {team.players?.length || 0}/{team.maxPlayers || 20} players • You: {team.coachRole}
                    </span>
                  </div>
                </div>

                <div className={styles.teamStats}>
                  <div className={styles.stat}>
                    <FaTrophy className={styles.statIcon} />
                    <span className={styles.statValue}>{getRecord(team)}</span>
                  </div>
                  <div className={styles.stat}>
                    <FaChartBar className={styles.statIcon} />
                    <span className={styles.statValue}>{getWinRate(team)}% Win</span>
                  </div>
                </div>

                <div className={styles.teamActions}>
                  <button 
                    className={styles.actionBtn}
                    onClick={(e) => handleRoster(e, team)}
                  >
                    Roster
                  </button>
                  <button 
                    className={styles.actionBtn}
                    onClick={(e) => handleStats(e, team)}
                  >
                    Stats
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {teams.length > 0 && (
        <div className={styles.footer}>
          <button className={styles.footerLink} onClick={handleViewAllTeams}>
            View All Teams <FaArrowRight />
          </button>
        </div>
      )}
    </div>
  );
};

export default MyTeamsWidget;

